import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListSubheader,
  FormHelperText,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import Header from '../Header';
import { getTicketApiBase } from '../../../utils/ticketApiBase';

const API_BASE = () => getTicketApiBase();

const AdminCoupons = ({ embedded = false }) => {
  const [coupons, setCoupons] = useState([]);
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    eventIds: [],
    ticketTypeIds: [],
    discountType: 'percent',
    discountValue: '',
    maxDiscountTickets: '',
    maxDiscount: '',
    maxUses: '',
    expiresAt: '',
    waivePlatformFee: false,
    platformFeeDiscountTickets: '',
  });

  const getEventLabel = (event) => event.title || event.event_name || `Event #${event.id}`;

  const getAvailableTicketTypes = (eventIds) => {
    const selected = new Set((eventIds || []).map(String));
    return events
      .filter((event) => selected.has(String(event.id)))
      .flatMap((event) =>
        (event.ticket_types || []).map((type) => ({
          ...type,
          eventLabel: getEventLabel(event),
        }))
      );
  };

  const getTicketTypeLabel = (type, { includeEvent = false } = {}) => {
    if (!type) return '';
    return includeEvent && type.eventLabel
      ? `${type.name} (${type.eventLabel})`
      : type.name;
  };

  useEffect(() => {
    fetchCoupons();
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events`);
      const data = await res.json();
      if (data.success) setEvents(data.events);
    } catch (e) {
      console.error('Failed to fetch events', e);
    }
  };

  const fetchCoupons = async () => {
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/coupons`);
      const data = await res.json();
      if (data.success) setCoupons(data.coupons);
    } catch (e) {
      console.error('Failed to fetch coupons', e);
    }
  };

  const handleOpenCreate = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      eventIds: events.length === 1 ? [String(events[0].id)] : [],
      ticketTypeIds: [],
      discountType: 'percent',
      discountValue: '',
      maxDiscountTickets: '',
      maxDiscount: '',
      maxUses: '',
      expiresAt: '',
      waivePlatformFee: false,
      platformFeeDiscountTickets: '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      eventIds: (coupon.events?.length
        ? coupon.events.map((event) => String(event.id))
        : coupon.event_id
          ? [String(coupon.event_id)]
          : []),
      ticketTypeIds: (coupon.ticket_types || []).map((type) => String(type.id)),
      discountType: coupon.discount_type,
      discountValue: coupon.discount_value.toString(),
      maxDiscountTickets: coupon.max_discount_tickets ? String(coupon.max_discount_tickets) : '',
      maxDiscount: coupon.max_discount_pence
        ? (coupon.max_discount_pence / 100).toFixed(2)
        : '',
      maxUses: coupon.max_uses ? coupon.max_uses.toString() : '',
      expiresAt: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      waivePlatformFee: !!coupon.waive_platform_fee,
      platformFeeDiscountTickets: coupon.platform_fee_discount_tickets
        ? String(coupon.platform_fee_discount_tickets)
        : '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.code || !formData.eventIds.length || !formData.discountValue) {
      setError('At least one event, code, and discount value are required');
      return;
    }

    const eventIds = formData.eventIds.map((id) => parseInt(id, 10));
    const maxDiscountPence = formData.discountType === 'percent' && formData.maxDiscount
      ? Math.round(parseFloat(formData.maxDiscount) * 100)
      : null;
    if (formData.discountType === 'percent' && formData.maxDiscount && (isNaN(maxDiscountPence) || maxDiscountPence < 1)) {
      setError('Max discount amount must be a valid amount in £');
      return;
    }

    const payload = {
      eventIds,
      ticketTypeIds: formData.ticketTypeIds.map((id) => parseInt(id, 10)),
      discountType: formData.discountType,
      discountValue: parseInt(formData.discountValue, 10),
      maxUses: formData.maxUses ? parseInt(formData.maxUses, 10) : null,
      expiresAt: formData.expiresAt || null,
      waivePlatformFee: formData.waivePlatformFee,
      maxDiscountTickets: formData.maxDiscountTickets ? parseInt(formData.maxDiscountTickets, 10) : null,
      maxDiscountPence,
      platformFeeDiscountTickets: formData.waivePlatformFee && formData.platformFeeDiscountTickets
        ? parseInt(formData.platformFeeDiscountTickets, 10)
        : null,
    };

    try {
      if (editingCoupon) {
        const res = await fetch(`${API_BASE()}/api/tickets/admin/coupons/${editingCoupon.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            active: editingCoupon.active,
            ...payload,
          }),
        });
        const data = await res.json();
        if (!data.success) { setError(data.message); return; }
      } else {
        const res = await fetch(`${API_BASE()}/api/tickets/admin/coupons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: formData.code,
            ...payload,
          }),
        });
        const data = await res.json();
        if (!data.success) { setError(data.message); return; }
      }
      setDialogOpen(false);
      fetchCoupons();
    } catch (e) {
      setError('Request failed');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await fetch(`${API_BASE()}/api/tickets/admin/coupons/${coupon.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          active: coupon.active ? 0 : 1,
          eventIds: (coupon.events || []).map((event) => event.id),
          ticketTypeIds: (coupon.ticket_types || []).map((type) => type.id),
          discountType: coupon.discount_type,
          discountValue: coupon.discount_value,
          maxUses: coupon.max_uses,
          expiresAt: coupon.expires_at,
          waivePlatformFee: !!coupon.waive_platform_fee,
          maxDiscountTickets: coupon.max_discount_tickets || null,
          maxDiscountPence: coupon.max_discount_pence || null,
          platformFeeDiscountTickets: coupon.platform_fee_discount_tickets || null,
        }),
      });
      fetchCoupons();
    } catch (e) {
      console.error('Toggle error', e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await fetch(`${API_BASE()}/api/tickets/admin/coupons/${id}`, { method: 'DELETE' });
      fetchCoupons();
    } catch (e) {
      console.error('Delete error', e);
    }
  };

  const availableTicketTypes = getAvailableTicketTypes(formData.eventIds);
  const groupTicketTypesByEvent = formData.eventIds.length > 1;
  const ticketTypeGroups = [];
  if (groupTicketTypesByEvent) {
    const seen = new Set();
    availableTicketTypes.forEach((type) => {
      const key = String(type.event_id);
      if (!seen.has(key)) {
        seen.add(key);
        ticketTypeGroups.push({
          eventId: key,
          eventLabel: type.eventLabel,
          types: availableTicketTypes.filter((item) => String(item.event_id) === key),
        });
      }
    });
  }

  const content = (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: embedded ? 0 : 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Discount Coupons
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Create Coupon
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#999' }}>Code</TableCell>
                <TableCell sx={{ color: '#999' }}>Events</TableCell>
                <TableCell sx={{ color: '#999' }}>Ticket types</TableCell>
                <TableCell sx={{ color: '#999' }}>Discount</TableCell>
                <TableCell sx={{ color: '#999' }}>Usage</TableCell>
                <TableCell sx={{ color: '#999' }}>Expires</TableCell>
                <TableCell sx={{ color: '#999' }}>Status</TableCell>
                <TableCell sx={{ color: '#999' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon) => (
                <TableRow key={coupon.id}>
                  <TableCell sx={{ color: '#fff', fontFamily: 'monospace', fontWeight: 700 }}>
                    {coupon.code}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(coupon.events?.length
                        ? coupon.events
                        : coupon.event_id
                          ? [{ id: coupon.event_id, title: coupon.event_title, event_name: coupon.event_name }]
                          : []
                      ).map((event) => (
                        <Chip
                          key={event.id}
                          label={getEventLabel(event)}
                          size="small"
                          sx={{ backgroundColor: 'rgba(102,126,234,0.15)', color: '#aab4ff' }}
                        />
                      ))}
                      {!coupon.events?.length && !coupon.event_id && '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(coupon.ticket_types || []).length ? (
                        coupon.ticket_types.map((type) => (
                          <Chip
                            key={type.id}
                            label={
                              (coupon.events?.length || 0) > 1
                                ? `${type.name} (${type.event_title || type.event_name || `Event #${type.event_id}`})`
                                : type.name
                            }
                            size="small"
                            sx={{ backgroundColor: 'rgba(255,193,7,0.12)', color: '#ffd54f' }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: '#999' }}>All types</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    <Box>
                      {coupon.discount_type === 'percent'
                        ? `${coupon.discount_value}%`
                        : `£${(coupon.discount_value / 100).toFixed(2)}`}
                      {coupon.discount_type === 'percent' && coupon.max_discount_pence > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#999' }}>
                          capped at £{(coupon.max_discount_pence / 100).toFixed(2)}
                        </Typography>
                      )}
                      {coupon.max_discount_tickets > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#999' }}>
                          max {coupon.max_discount_tickets} ticket{coupon.max_discount_tickets === 1 ? '' : 's'}
                        </Typography>
                      )}
                      {!!coupon.waive_platform_fee && (
                        <Chip
                          label={coupon.platform_fee_discount_tickets
                            ? `Waives fee (${coupon.platform_fee_discount_tickets} ticket${coupon.platform_fee_discount_tickets === 1 ? '' : 's'})`
                            : 'Waives fee'}
                          size="small"
                          sx={{ mt: 0.5, backgroundColor: 'rgba(76,175,80,0.15)', color: '#4caf50', height: 20, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {coupon.used_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={coupon.active ? 'Active' : 'Inactive'}
                      size="small"
                      onClick={() => handleToggleActive(coupon)}
                      sx={{
                        backgroundColor: coupon.active ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                        color: coupon.active ? '#4caf50' : '#f44336',
                        cursor: 'pointer',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenEdit(coupon)} sx={{ color: '#667eea' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(coupon.id)} sx={{ color: '#f44336' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {coupons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                    No coupons created yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}>
          <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel sx={{ color: '#999' }}>Events</InputLabel>
              <Select
                multiple
                value={formData.eventIds}
                onChange={(e) => {
                  const eventIds = e.target.value;
                  const allowedTypeIds = new Set(
                    getAvailableTicketTypes(eventIds).map((type) => String(type.id))
                  );
                  setFormData({
                    ...formData,
                    eventIds,
                    ticketTypeIds: formData.ticketTypeIds.filter((id) => allowedTypeIds.has(id)),
                  });
                }}
                input={<OutlinedInput label="Events" />}
                renderValue={(selected) => selected
                  .map((id) => {
                    const event = events.find((item) => String(item.id) === id);
                    return event ? getEventLabel(event) : id;
                  })
                  .join(', ')}
                sx={{ color: '#fff' }}
              >
                {events.map((event) => (
                  <MenuItem key={event.id} value={String(event.id)}>
                    <Checkbox checked={formData.eventIds.includes(String(event.id))} />
                    <ListItemText primary={getEventLabel(event)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!formData.eventIds.length}>
              <InputLabel shrink sx={{ color: '#999' }}>Ticket types</InputLabel>
              <Select
                multiple
                value={formData.ticketTypeIds}
                onChange={(e) => setFormData({ ...formData, ticketTypeIds: e.target.value })}
                input={<OutlinedInput label="Ticket types" notched />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected.length) return 'All ticket types';
                  return selected
                    .map((id) => {
                      const type = availableTicketTypes.find((item) => String(item.id) === id);
                      return type
                        ? getTicketTypeLabel(type, { includeEvent: groupTicketTypesByEvent })
                        : id;
                    })
                    .join(', ');
                }}
                sx={{ color: '#fff' }}
              >
                {groupTicketTypesByEvent
                  ? ticketTypeGroups.flatMap((group) => [
                      <ListSubheader
                        key={`group-${group.eventId}`}
                        sx={{ backgroundColor: '#1a1a1a', color: '#999', lineHeight: '32px' }}
                      >
                        {group.eventLabel}
                      </ListSubheader>,
                      ...group.types.map((type) => (
                        <MenuItem key={type.id} value={String(type.id)}>
                          <Checkbox checked={formData.ticketTypeIds.includes(String(type.id))} />
                          <ListItemText primary={type.name} />
                        </MenuItem>
                      )),
                    ])
                  : availableTicketTypes.map((type) => (
                      <MenuItem key={type.id} value={String(type.id)}>
                        <Checkbox checked={formData.ticketTypeIds.includes(String(type.id))} />
                        <ListItemText primary={type.name} />
                      </MenuItem>
                    ))}
              </Select>
              <FormHelperText sx={{ color: '#777' }}>
                {!formData.eventIds.length
                  ? 'Select events first'
                  : !availableTicketTypes.length
                    ? 'No ticket types found for the selected events'
                    : 'Leave empty to apply the coupon to all ticket types'}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              label="Discount Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              disabled={!!editingCoupon}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: '#999' }}>Discount Type</InputLabel>
              <Select
                value={formData.discountType}
                onChange={(e) => setFormData({
                  ...formData,
                  discountType: e.target.value,
                  maxDiscount: e.target.value === 'percent' ? formData.maxDiscount : '',
                })}
                label="Discount Type"
                sx={{ color: '#fff' }}
              >
                <MenuItem value="percent">Percentage (%)</MenuItem>
                <MenuItem value="fixed">Fixed Amount (pence)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={formData.discountType === 'percent' ? 'Discount (%)' : 'Discount (pence)'}
              type="number"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
            {formData.discountType === 'percent' && (
              <TextField
                fullWidth
                label="Max discount amount (£)"
                type="number"
                value={formData.maxDiscount}
                onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                helperText="Optional cap. Discount = min(% of ticket price, this amount). Leave empty for no cap."
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
                FormHelperTextProps={{ sx: { color: '#777' } }}
              />
            )}
            <TextField
              fullWidth
              label="Max tickets to discount"
              type="number"
              value={formData.maxDiscountTickets}
              onChange={(e) => setFormData({ ...formData, maxDiscountTickets: e.target.value })}
              helperText="Optional. Extra tickets beyond this are charged at full price. Leave empty to discount all tickets."
              inputProps={{ min: 1, step: 1 }}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
              FormHelperTextProps={{ sx: { color: '#777' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.waivePlatformFee}
                  onChange={(e) => setFormData({
                    ...formData,
                    waivePlatformFee: e.target.checked,
                    platformFeeDiscountTickets: e.target.checked ? formData.platformFeeDiscountTickets : '',
                  })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                  }}
                />
              }
              label="Discount the platform fee"
              sx={{ color: '#ccc', display: 'block', mb: formData.waivePlatformFee ? 1 : 2 }}
            />
            {formData.waivePlatformFee && (
              <TextField
                fullWidth
                label="Tickets with discounted platform fee"
                type="number"
                value={formData.platformFeeDiscountTickets}
                onChange={(e) => setFormData({ ...formData, platformFeeDiscountTickets: e.target.value })}
                helperText="For per-ticket platform fees: how many tickets get the fee discounted. Extra tickets pay the full fee. Leave empty to discount all."
                inputProps={{ min: 1, step: 1 }}
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
                FormHelperTextProps={{ sx: { color: '#777' } }}
              />
            )}
            <TextField
              fullWidth
              label="Max Uses (leave empty for unlimited)"
              type="number"
              value={formData.maxUses}
              onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
            <TextField
              fullWidth
              label="Expires At"
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true, sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} sx={{ color: '#999' }}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {editingCoupon ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );

  if (embedded) return content;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#121210' }}>
      <Header title="Coupon Management" />
      {content}
    </Box>
  );
};

export default AdminCoupons;
